import { useState } from "react";
import { HttpError, request } from "../../../api/http";
import { useConfirm } from "../../../components/Dialog";
import { useAuth } from "../../../context/AuthContext";
import { SubscriptionDetailResponse } from "../../../types/subscription";
import PaymentMethodsDrawer from "./PaymentMethodsDrawer";
import PendingAnnualitiesDrawer from "./PendingAnnualitiesDrawer";
import ReduceCapitalDrawer from "./ReduceCapitalDrawer";

type Props = {
  data: SubscriptionDetailResponse;
  onReload: () => void;
};

type Busy =
  | null
  | "pausing"
  | "resuming"
  | "to-yearly"
  | "to-monthly"
  | "recalculating"
  | "sending-insurer";

const tomorrowISO = (): string => {
  const t = new Date();
  t.setDate(t.getDate() + 1);
  return t.toISOString().slice(0, 10);
};

export default function SubscriptionActionsBar({ data, onReload }: Props) {
  const { token } = useAuth();
  const confirm = useConfirm();
  const [busy, setBusy] = useState<Busy>(null);
  const [feedback, setFeedback] = useState<
    | { kind: "success" | "error"; message: string }
    | null
  >(null);
  const [pauseDate, setPauseDate] = useState<string>(tomorrowISO());
  const [pauseMenuOpen, setPauseMenuOpen] = useState(false);
  const [pmDrawerOpen, setPmDrawerOpen] = useState(false);
  const [reduceCapitalOpen, setReduceCapitalOpen] = useState(false);
  const [pendingAnnOpen, setPendingAnnOpen] = useState(false);

  const { subscription, leadUlid, leadStatus } = data;
  const isPaused = subscription.status === "PAUSED";
  const isMonthly = subscription.frequency === 12;
  const carrier = subscription.carrier;

  const showSendToInsurer =
    leadStatus === "ops_review" &&
    (carrier === "AXA" || carrier === "AXA_WL");

  const post = async (
    path: string,
    body: Record<string, unknown> | undefined,
    busyKey: Busy,
    successMsg: string,
  ) => {
    if (busy) return;
    setBusy(busyKey);
    setFeedback(null);
    try {
      await request<Record<string, unknown>>(path, {
        method: "POST",
        token,
        body: body ?? {},
      });
      setFeedback({ kind: "success", message: successMsg });
      onReload();
    } catch (err) {
      setFeedback({
        kind: "error",
        message: err instanceof HttpError
          ? `HTTP ${err.status}: ${
              ((err.body as { message?: string })?.message ?? "").slice(0, 200)
            }`
          : "Acción falló",
      });
    } finally {
      setBusy(null);
    }
  };

  const handlePause = async () => {
    if (!pauseDate) return;
    setPauseMenuOpen(false);
    await post(
      `/api/admin/operations/subscriptions/${subscription.id}/pause`,
      { pauseDate },
      "pausing",
      `Pausada hasta ${pauseDate}`,
    );
  };

  const handleResume = async () => {
    const ok = await confirm({
      title: "Reactivar suscripción",
      message: "¿Reactivar esta suscripción?",
      confirmLabel: "Reactivar",
    });
    if (!ok) return;
    await post(
      `/api/admin/operations/subscriptions/${subscription.id}/resume`,
      undefined,
      "resuming",
      "Suscripción reactivada",
    );
  };

  // Reglas de Ops 2026-08-06: el cambio de periodicidad SIEMPRE pasa por un
  // modal con los datos reales del preview. Anual→mensual dentro de los 30
  // días tras el pago del recibo anual = se DEVUELVE ese recibo (confirmación
  // explícita); pasada la ventana, el cambio aplica a la siguiente anualidad.
  // Mensual→anual = un único recibo por los meses pendientes de la anualidad.
  const handleChangeFreq = async (target: "yearly" | "monthly") => {
    type FreqPreview = {
      mode?: "REFUND_AND_RESTART" | "NEXT_ANNUITY";
      annualAmount?: number | null;
      annualPaidAt?: string | null;
      daysSincePayment?: number | null;
      windowDays?: number;
      pendingMonths?: number;
      totalAmount?: number | null;
    };
    let preview: FreqPreview;
    try {
      preview = await request<FreqPreview>(
        `/api/admin/operations/subscriptions/${subscription.id}/change-frequency/preview?target=${target}`,
        { token },
      );
    } catch (err) {
      setFeedback({
        kind: "error",
        message: err instanceof HttpError
          ? `No se pudo obtener el preview (HTTP ${err.status})`
          : "No se pudo obtener el preview del cambio",
      });
      return;
    }

    let title: string;
    let message: string;
    let confirmLabel: string;
    if (target === "yearly") {
      const amount = preview.totalAmount != null ? `${preview.totalAmount.toFixed(2)} €` : "importe no disponible";
      title = "Cambiar a YEARLY";
      message =
        `Se respetan los recibos ya cobrados y se genera UN único recibo por los ` +
        `${preview.pendingMonths ?? "?"} meses pendientes de la anualidad, por un total de ${amount}. ` +
        `Las siguientes anualidades pasan a recibo anual.`;
      confirmLabel = `Generar recibo de ${preview.pendingMonths ?? "?"} meses`;
    } else if (preview.mode === "REFUND_AND_RESTART") {
      const amount = preview.annualAmount != null ? `${preview.annualAmount.toFixed(2)} €` : "importe no disponible";
      title = "Cambiar a MONTHLY — con devolución del recibo anual";
      message =
        `El recibo anual de ${amount} (cobrado el ${preview.annualPaidAt ?? "?"}, hace ` +
        `${preview.daysSincePayment ?? "?"} días, dentro de la ventana de ${preview.windowDays ?? 30}) ` +
        `SE DEVOLVERÁ al cliente y se generarán los 12 recibos mensuales de la anualidad. ` +
        `Esta acción mueve dinero: confirma solo si procede.`;
      confirmLabel = "Devolver recibo anual y cambiar a MONTHLY";
    } else {
      title = "Cambiar a MONTHLY — desde la siguiente anualidad";
      message =
        `El recibo anual se cobró hace más de ${preview.windowDays ?? 30} días: NO se devuelve. ` +
        `La anualidad actual se mantiene pagada y anual, y los recibos mensuales ` +
        `comienzan en la siguiente anualidad.`;
      confirmLabel = "Cambiar a MONTHLY (sin devolución)";
    }

    const ok = await confirm({ title, message, confirmLabel, tone: "warning" });
    if (!ok) return;
    await post(
      `/api/admin/operations/subscriptions/${subscription.id}/change-frequency`,
      { target, accept: true },
      target === "yearly" ? "to-yearly" : "to-monthly",
      `Frecuencia cambiada a ${target.toUpperCase()}`,
    );
  };

  const handleRecalculate = async () => {
    const ok = await confirm({
      title: "Regenerar suscripción",
      message:
        "¿Regenerar las facturas de esta suscripción? Recalcula la cadena "
        + "de fees del lead y reescribe los scheduled invoices pendientes. "
        + "Aplícalo si han cambiado pricing, decisiones, capital o frecuencia.",
      confirmLabel: "Regenerar",
      tone: "warning",
    });
    if (!ok) return;
    await post(
      `/api/admin/operations/subscriptions/${subscription.id}/recalculate`,
      undefined,
      "recalculating",
      "Suscripción regenerada",
    );
  };

  const handleSendToInsurer = async () => {
    if (!leadUlid) return;
    const ok = await confirm({
      title: "Enviar a la aseguradora",
      message: "Enviar este lead a la aseguradora. Esta acción es irreversible.",
      confirmLabel: "Enviar",
      tone: "danger",
    });
    if (!ok) return;
    await post(
      `/api/admin/operations/leads/${leadUlid}/send-to-insurer`,
      undefined,
      "sending-insurer",
      "Lead enviado a la aseguradora",
    );
  };

  return (
    <section
      style={{
        background: "white",
        borderRadius: "0.55rem",
        boxShadow: "0 1px 3px rgba(0,0,0,.04)",
        padding: "0.7rem 0.95rem",
        marginBottom: "0.85rem",
      }}
    >
      <h3
        style={{
          margin: "0 0 0.55rem",
          fontSize: ".72rem",
          fontWeight: 700,
          color: "#73879C",
          textTransform: "uppercase",
          letterSpacing: ".4px",
        }}
      >
        Acciones
      </h3>
      <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", alignItems: "center" }}>
        {isPaused ? (
          <ActionBtn
            onClick={handleResume}
            disabled={!!busy}
            loading={busy === "resuming"}
            tone="primary"
          >
            Reactivar suscripción
          </ActionBtn>
        ) : (
          <div style={{ position: "relative" }}>
            <ActionBtn
              onClick={() => setPauseMenuOpen((v) => !v)}
              disabled={!!busy}
              loading={busy === "pausing"}
              tone="primary"
            >
              Pausar suscripción
            </ActionBtn>
            {pauseMenuOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 4px)",
                  left: 0,
                  zIndex: 20,
                  background: "white",
                  border: "1px solid #dee2e6",
                  borderRadius: "0.5rem",
                  boxShadow: "0 6px 16px rgba(0,0,0,.12)",
                  padding: "0.6rem 0.7rem",
                  minWidth: 230,
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.4rem",
                }}
              >
                <label
                  style={{
                    fontSize: ".72rem",
                    fontWeight: 600,
                    color: "#73879C",
                  }}
                >
                  Pausar hasta
                </label>
                <input
                  type="date"
                  value={pauseDate}
                  min={tomorrowISO()}
                  onChange={(e) => setPauseDate(e.target.value)}
                  style={{
                    padding: "0.35rem 0.5rem",
                    border: "1px solid #dee2e6",
                    borderRadius: "0.35rem",
                    fontSize: ".82rem",
                    background: "#f8f9fb",
                  }}
                />
                <button
                  type="button"
                  onClick={handlePause}
                  disabled={!pauseDate}
                  style={confirmPauseBtn}
                >
                  Confirmar pausa
                </button>
              </div>
            )}
          </div>
        )}

        {isMonthly ? (
          <ActionBtn
            onClick={() => handleChangeFreq("yearly")}
            disabled={!!busy}
            loading={busy === "to-yearly"}
          >
            Cambiar a YEARLY
          </ActionBtn>
        ) : (
          <ActionBtn
            onClick={() => handleChangeFreq("monthly")}
            disabled={!!busy}
            loading={busy === "to-monthly"}
          >
            Cambiar a MONTHLY
          </ActionBtn>
        )}

        {/*
          No carrier/version gate on purpose: the backend handler
          (RecalculateInvoicesOfSubscriptionCommandHandler) has no
          restriction — accepts any subscription and rebuilds invoices
          via FeeClient. The legacy template CSS-hides this for non-AXA-v8
          / non-AXA-WL-v2 subs as a soft safety net, but ops needs it
          visible for all subs in the new admin. The confirm modal is
          the remaining safety net.
        */}
        <ActionBtn
          onClick={handleRecalculate}
          disabled={!!busy}
          loading={busy === "recalculating"}
        >
          Regenerar suscripción
        </ActionBtn>

        <ActionBtn
          onClick={() => setPmDrawerOpen(true)}
          disabled={!!busy}
        >
          Métodos de pago
        </ActionBtn>
        {carrier !== "SCOR" && (
          <ActionBtn
            onClick={() => setReduceCapitalOpen(true)}
            disabled={!!busy}
          >
            Reducir capital
          </ActionBtn>
        )}

        <ActionBtn
          onClick={() => setPendingAnnOpen(true)}
          disabled={!!busy}
        >
          Actualizar importes por año
        </ActionBtn>

        {showSendToInsurer && (
          <ActionBtn
            onClick={handleSendToInsurer}
            disabled={!!busy}
            loading={busy === "sending-insurer"}
            tone="success"
          >
            Enviar a aseguradora
          </ActionBtn>
        )}
      </div>

      {feedback && (
        <div
          style={{
            marginTop: "0.55rem",
            padding: "0.45rem 0.65rem",
            borderRadius: "0.4rem",
            fontSize: ".78rem",
            background: feedback.kind === "success" ? "#d4edda" : "#fde2e2",
            color: feedback.kind === "success" ? "#155724" : "#b32424",
          }}
        >
          {feedback.message}
        </div>
      )}

      {pmDrawerOpen && (
        <PaymentMethodsDrawer
          subscriptionId={subscription.id}
          leadUlid={leadUlid}
          currentDefault={subscription.defaultPaymentMethod}
          onClose={() => setPmDrawerOpen(false)}
          onChanged={onReload}
        />
      )}

      {reduceCapitalOpen && (
        <ReduceCapitalDrawer
          subscription={subscription}
          onClose={() => setReduceCapitalOpen(false)}
          onApplied={() => {
            setReduceCapitalOpen(false);
            setFeedback({ kind: "success", message: "Capital reducido" });
            onReload();
          }}
        />
      )}

      {pendingAnnOpen && (
        <PendingAnnualitiesDrawer
          subscription={subscription}
          onClose={() => setPendingAnnOpen(false)}
          onApplied={() => {
            setPendingAnnOpen(false);
            setFeedback({
              kind: "success",
              message: "Importes de los recibos futuros actualizados",
            });
            onReload();
          }}
        />
      )}
    </section>
  );
}

function ActionBtn({
  onClick,
  disabled,
  loading,
  tone,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  tone?: "primary" | "success";
  children: React.ReactNode;
}) {
  const bg =
    tone === "primary" ? "#0069d9" : tone === "success" ? "#1a7f37" : "#f8f9fb";
  const fg = tone === "primary" || tone === "success" ? "white" : "#1f2933";
  const border =
    tone === "primary" ? "#0069d9" : tone === "success" ? "#1a7f37" : "#dee2e6";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "0.4rem 0.85rem",
        borderRadius: "0.4rem",
        background: bg,
        color: fg,
        border: `1px solid ${border}`,
        fontSize: ".8rem",
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled && !loading ? 0.55 : 1,
      }}
    >
      {loading ? "…" : children}
    </button>
  );
}

const confirmPauseBtn: React.CSSProperties = {
  padding: "0.4rem 0.85rem",
  borderRadius: "0.4rem",
  background: "#0069d9",
  color: "white",
  border: "1px solid #0069d9",
  fontSize: ".8rem",
  fontWeight: 600,
  cursor: "pointer",
};
